import { useEffect, useState, useCallback } from "react";
import { Sparkles, X, Check, AlertTriangle } from "lucide-react";

/**
 * WhatsNewModal
 *
 * A full-screen, transparent (blurred glass) modal that announces new app
 * features. Feature lines are loaded dynamically from a remote text file and
 * rendered one item per line. The modal is shown only once per "feature
 * iteration": it tracks the last-seen version in localStorage and re-appears
 * only when a new iteration is published.
 *
 * How the "version" is determined (in priority order):
 *   1. An explicit version marker on the first non-empty line of the file,
 *      written as `# version: <value>` or `#v<value>` (e.g. `# version: 2026-09`).
 *   2. Otherwise, a stable hash of the file's feature content. Any change to
 *      the feature list produces a new version, so editing features.txt is
 *      enough to re-surface the modal for everyone.
 *
 * Props:
 * Display cadence: the modal appears once per browser session and keeps
 * re-appearing on future sessions/logins until a NEW feature iteration is
 * published (i.e. the version changes). Dismissing it only silences it for the
 * current session, not forever.
 *
 * Props:
 *   - src:        URL of the features file (default: "/features.txt")
 *   - title:      Heading text (default: "What's New")
 *   - storageKey: key used for version tracking
 */
const DEFAULT_SRC = "/features.txt";
const DEFAULT_STORAGE_KEY = "whatsNew:seenVersion";

// Small, stable, dependency-free string hash (djb2). Returns a short hex string.
const hashString = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  // Convert to unsigned 32-bit and hex.
  return (h >>> 0).toString(16);
};

// A line that switches the parser into the "always do" section, e.g. "ALWAYS DO's".
const ALWAYS_HEADING = /^always\s*do'?s?\b/i;

// Parse the raw file text into { version, features, alwaysDos }.
//  - Lines before the "ALWAYS DO's" heading are normal feature items.
//  - Lines after it are must-do reminders (rendered in red, always shown).
const parseFeatures = (text) => {
  const rawLines = text.split(/\r?\n/);
  let explicitVersion = null;
  const features = [];
  const alwaysDos = [];
  let inAlways = false;

  rawLines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    // Detect an explicit version marker on a comment line, e.g.
    //   "# version: 2026-09-19"  or  "#v3"
    if (trimmed.startsWith("#")) {
      if (idx === 0 || explicitVersion === null) {
        const m = trimmed.match(/#\s*(?:version[:=]?\s*|v)\s*(.+)$/i);
        if (m && m[1]) explicitVersion = m[1].trim();
      }
      return; // comment lines are never shown as features
    }

    // Switch into the "always do" section when we hit its heading.
    if (ALWAYS_HEADING.test(trimmed)) { inAlways = true; return; }

    if (inAlways) alwaysDos.push(trimmed);
    else features.push(trimmed);
  });

  // Version reflects both lists so any content change re-surfaces the modal.
  const version = explicitVersion || hashString([...features, "|ALWAYS|", ...alwaysDos].join("\n"));
  return { version, features, alwaysDos };
};

export default function WhatsNewModal({
  src = DEFAULT_SRC,
  title = "What's New",
  storageKey = DEFAULT_STORAGE_KEY,
}) {
  const [open, setOpen] = useState(false);
  const [features, setFeatures] = useState([]);
  const [alwaysDos, setAlwaysDos] = useState([]);
  const [version, setVersion] = useState("");

  useEffect(() => {
    let alive = true;
    // Cache-bust so users get the freshly published list, not a stale cache.
    const url = `${src}${src.includes("?") ? "&" : "?"}t=${Date.now()}`;
    fetch(url, { cache: "no-store" })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((text) => {
        if (!alive) return;
        const parsed = parseFeatures(text);
        if (parsed.features.length === 0 && parsed.alwaysDos.length === 0) return; // nothing to announce

        // Show once per session; reappears next session until the version changes.
        let seenThisSession = null;
        try { seenThisSession = sessionStorage.getItem(storageKey); } catch { /* storage unavailable */ }

        setFeatures(parsed.features);
        setAlwaysDos(parsed.alwaysDos);
        setVersion(parsed.version);
        if (seenThisSession !== parsed.version) setOpen(true);
      })
      .catch(() => { /* fail silently: never block the app on the announcement */ });
    return () => { alive = false; };
  }, [src, storageKey]);

  const dismiss = useCallback(() => {
    // Silence only for this session; a new session will show it again until
    // a new version is published.
    try { if (version) sessionStorage.setItem(storageKey, version); } catch { /* ignore */ }
    setOpen(false);
  }, [version, storageKey]);

  // Close on Escape for accessibility/keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      data-testid="whats-new-modal"
      onClick={dismiss}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(5, 26, 17, 0.55)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="glass-panel relative w-full max-w-3xl rounded-2xl border border-gold shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close"
          data-testid="whats-new-close"
          className="absolute right-4 top-4 text-emerald-muted hover:text-gold transition"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-10 md:p-12">
          <div className="overline mb-2">Just Shipped</div>
          <h2 id="whats-new-title" className="font-heading text-5xl text-white inline-flex items-center gap-3">
            <Sparkles className="w-9 h-9 text-gold" /> {title}
          </h2>
          <p className="text-emerald-muted mt-3 text-base">Here's what we've added to make your day smoother.</p>

          {alwaysDos.length > 0 && (
            <div
              className="mt-6 rounded-xl border border-red-500/60 bg-[rgba(211,47,47,0.12)] p-5"
              data-testid="whats-new-always-dos"
            >
              <div className="flex items-center gap-2 text-red-400 font-bold uppercase tracking-[0.18em] text-xs">
                <AlertTriangle className="w-4 h-4" /> Always Do's
              </div>
              <ul className="mt-3 space-y-2">
                {alwaysDos.map((d, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`whats-new-alwaysdo-${i}`}>
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <span className="text-base font-semibold text-red-400 leading-relaxed">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul className="mt-8 space-y-4 overflow-y-auto pr-2" style={{ maxHeight: "55vh" }}>
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`whats-new-item-${i}`}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[rgba(197,160,89,0.15)] border border-gold-subtle">
                  <Check className="w-3.5 h-3.5 text-gold" />
                </span>
                <span className="text-base text-emerald-text leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex justify-end">
            <button
              type="button"
              onClick={dismiss}
              data-testid="whats-new-dismiss"
              className="gold-button px-8 h-12 rounded-md inline-flex items-center gap-2 text-base"
            >
              <Sparkles className="w-5 h-5" /> Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
